import { useEffect, useState } from "react";
import CandidateCard from "./CandidateCard";
import { candidateData } from "../model";
import { baseUrl, Fetch } from "../../../shared/Fetch";
import { ILogin } from "../../../models/Login";
import { Skeleton } from "primereact/skeleton";

interface CompensationRange {
    min: number;
    max: number;
}

interface WorkPreference {
    annualSalary: CompensationRange;
    hourlyRate: CompensationRange;
    willingToRelocate: boolean;
}

interface Skill {
    skill: string;
    yearsUsed: number;
}

interface CandidateProfile {
    candidateId: string,
    city: string;
    region: string;

    fullName: string;
    firstName: string;
    lastName: string;

    usCitizen: boolean;

    currentJobTitle: string;
    currentCompany: string;

    yearsOfExperience: number;

    dateLastActive: string;

    likelyToMove: boolean;

    desiredWorkSettings: string[];

    workPermitDocuments: string[];

    workPreferences: WorkPreference[];

    skills: Skill[];
}

interface IProps {
    body: any;
    reload: boolean;
    loading: boolean,
    setLoading: (is: boolean) => void,
    toast: any;
    setUseageData: (usage: { customerId?: number, quota?: number, viewsConsumed?: number, viewsRemaining?: number }) => void,
    setViewSideFilter: (viewSideFilter: boolean) => void,
}

function Pagination({ currentPage, totalPages, resultsPerPage, onPageChange, onResultsPerPageChange, toast }: { currentPage: number; totalPages: number; resultsPerPage: number; onPageChange: (page: number) => void; onResultsPerPageChange: (n: number) => void; toast: any }) {
    const btnStyle = (disabled: boolean): React.CSSProperties => ({
        background: "none",
        border: "none",
        cursor: disabled ? "default" : "pointer",
        color: disabled ? "#ccc" : "#0d7a8a",
        fontSize: "18px",
        padding: "0 4px",
        lineHeight: 1,
    });

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 0",
                borderTop: "1px solid #e5e7eb",
                fontFamily: "sans-serif",
                fontSize: "14px",
            }}
        >
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={{ display: "flex", alignItems: "center", gap: "6px", color: "#0d7a8a", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>
                Back to top ↑
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <button onClick={() => onPageChange(1)} disabled={currentPage === 1} style={btnStyle(currentPage === 1)}>
                    ⏮
                </button>
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={btnStyle(currentPage === 1)}>
                    ‹
                </button>

                <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "#333" }}>
                    <span
                        style={{
                            background: "#1a1a2e",
                            color: "white",
                            borderRadius: "50%",
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "13px",
                        }}
                    >
                        {currentPage}
                    </span>
                    of {totalPages}
                </span>

                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={btnStyle(currentPage === totalPages)}>
                    ›
                </button>
                <button onClick={() => {
                    toast.current.show({ severity: 'warning', detail: "Page Can Not Exceed More Than 50", life: 3000 });
                    onPageChange(50);
                }} disabled={currentPage === totalPages} style={btnStyle(currentPage === totalPages)}>
                    ⏭
                </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#333" }}>
                <span>Results per page</span>
                <select value={resultsPerPage} onChange={(e) => onResultsPerPageChange(Number(e.target.value))} style={{ border: "2px solid #0d7a8a", borderRadius: "6px", padding: "4px 8px", color: "#333", cursor: "pointer", outline: "none" }}>
                    {[10, 25, 50, 100].map((n) => (
                        <option key={n} value={n}>
                            {n}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}

export const Table = (props: IProps) => {
    const [tableData, setTableData] = useState<Array<CandidateProfile>>([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingViewHistory, setLoadingViewHistory] = useState<boolean>(false);
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const startIdx = (currentPage - 1) * pageSize;
    const paginatedData = tableData.slice(startIdx, startIdx + pageSize);

    let _roles: Array<number> = [];
    const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
    loginObj.roles.forEach((role) => {
        _roles.push(role.role_ID);
    });

    const loadTableData = async () => {
        setTableData(tableData);
        const token = JSON.parse(sessionStorage.getItem("auth") || "{}")?.token;

        props.setLoading(true);

        let newBody: any = { ...props.body, page: currentPage, pageSize: pageSize };

        try {
            const response = await fetch(`${baseUrl}MassMail/searchByPost`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ searchParameters: newBody }), // send JSON body
            });

            if (response.status === 401) {
                props.setLoading(false);
                props?.toast?.current?.show({
                    severity: "error",
                    detail: "Unauthorized! Please login.",
                    life: 2000,
                });
                // alert("Unauthorized! Please login.");
                window.location.href = "/login";
                return;
            }

            if (!response.ok) {
                props.setLoading(false);
                const message = await response.text();
                // alert("Error: " + message);
                props?.toast?.current?.show({
                    severity: "error",
                    detail: "Error: " + message,
                    life: 2000,
                });
                return;
            }

            // Parse JSON response from backend
            const data = await response.json();
            setTableData(data?.data);
            setTotalCount(data?.meta?.totalCount);
            if (_roles.includes(1)) {
                props.setUseageData(data?.usage);
            }
            props.setLoading(false);
        } catch (err) {
            props.setLoading(false);
            // alert("Something went wrong while fetching profiles.");
            props?.toast?.current?.show({
                severity: "error",
                detail: "Something went wrong while fetching profiles.",
                life: 2000,
            });
        }
        props.setLoading(false);
        // const response = await Fetch("MassMail/searchByPost", "POST", props.body, null, props.toast);
        // if (response) {
        //     // const data: Array<CandidateProfile> = await response.json();
        //     const data: any = await response.json();

        //     console.log(data);

        //     // setTableData(data);
        // } else setTableData(tableData);
    };

    const CandidateSkeleton = () => {
        return (
            <div className="card">
                <div className="border-round border-1 surface-border p-4 surface-card">
                    <div className="flex mb-3">
                        <Skeleton shape="circle" size="4rem" className="mr-2" />
                        <div>
                            <Skeleton width="10rem" className="mb-2" />
                            <Skeleton width="5rem" className="mb-2" />
                            <Skeleton height=".5rem" />
                        </div>
                    </div>

                    <Skeleton width="100%" height="150px" />

                    <div className="flex justify-content-between mt-3">
                        <Skeleton width="4rem" height="2rem" />
                        <Skeleton width="4rem" height="2rem" />
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (props.body.q !== undefined && props.body.q !== null && props.body.q?.trim() !== "") loadTableData();
    }, [props.body, props.reload, currentPage, pageSize]);

    useEffect(() => {
        if (tableData.length > 0) {
            props.setViewSideFilter(true);
        }
    }, [tableData]);

    return (
        <>
            {/* {tableData.map((candidate, index) => (
        <CandidateCard key={index} candidate={candidate} />
      ))} */}
            {props.loading === true ? (
                Array.from({ length: pageSize }).map((_, i) => <CandidateSkeleton key={i} />)
            ) :
                <>
                    {tableData.map((candidate, index) => (
                        <CandidateCard key={startIdx + index} candidate={candidate} setUseageData={props.setUseageData} loadingViewHistory={loadingViewHistory} setLoadingViewHistory={setLoadingViewHistory} filters={props.body} />
                    ))}
                </>}

            {(tableData.length > 0) && <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                resultsPerPage={pageSize}
                onPageChange={(page) => {
                    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
                    window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                onResultsPerPageChange={(n) => {
                    setPageSize(n);
                    setCurrentPage(1);
                }}
                toast={props.toast}
            />}
        </>
    );
};
